import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDebouncedCallback } from 'use-debounce';
import Field from './Field';
import Constants from '../../utils/Constants';
import { extendedStateType } from '../../types';
import { resolveValue } from '../../utils/fields';
import { NO_STATE } from '../../hooks/useExtendedState';
import './_styles_/TextAreaField.css';
import useEffectOnUpdate from "../../hooks/useEffectOnUpdate";

TextAreaField.propTypes = {
    disabled: PropTypes.bool,
    label: PropTypes.node,
    inputId: PropTypes.string,
    onChange: PropTypes.func,
    validator: PropTypes.func,
    onValidated: PropTypes.func,
    className: PropTypes.string,
    wrapperClassName: PropTypes.string,
    placeholder: PropTypes.string,
    maxLength: PropTypes.number,
    rows: PropTypes.number,
    isRequired: PropTypes.bool,
    state: extendedStateType,
    textInstructions: PropTypes.element
};

function TextAreaField(props) {
    const {
        disabled = false,
        label = null,
        inputId = null,
        onChange = undefined,
        validator = null,
        onValidated = undefined,
        className = '',
        wrapperClassName = '',
        placeholder = null,
        maxLength = null,
        rows = 8,
        isRequired = false,
        state = NO_STATE,
        textInstructions = undefined
    } = props;
    const resolvedValue = resolveValue(undefined, state);
    const [displayedValue, setDisplayedValue] = useState(resolvedValue);
    const [error, setError] = useState(null);
    const stateValueSetter = state && state !== NO_STATE ? state.setValueIfNew : () => {};
    const onValidationComplete = (newError, sanitizedValue) => {
        onValidated?.(sanitizedValue, validator === null ? null : !newError);
        setError(newError);
    };
    const debouncedValidation = useDebouncedCallback((newValue) => {
        validator?.(newValue)
            .then(() => onValidationComplete(null, newValue))
            .catch((e) => onValidationComplete(e, newValue));
    }, Constants.DISPLAY_FIELD_ERROR_DELAY_IN_MS);
    const setNewValue = (newValue) => {
        setDisplayedValue(newValue);
        stateValueSetter(newValue);
        onChange?.(newValue);
        if (validator) {
            debouncedValidation(newValue);
        } else {
            onValidationComplete(null, newValue);
        }
    };
    useEffectOnUpdate(() => {
        setNewValue(state.value);
    }, [state.value]);
    return (
        <Field
            label={label}
            className={['field--multiline', wrapperClassName].filter(Boolean).join(' ')}
            inputId={inputId}
            error={error}
            state={state}
            isRequired={isRequired}
            isRequiredEmpty={isRequired && !displayedValue}
        >
            <div className="text-area-field--container">
                <textarea
                    id={inputId}
                    className={`text-area-field ${inputId} ${className}`}
                    value={displayedValue || ''}
                    onChange={(event) => setNewValue(event.target.value)}
                    placeholder={placeholder}
                    rows={rows}
                    maxLength={maxLength}
                    aria-required={isRequired}
                    disabled={disabled}
                />
                {textInstructions && <div className="text-area-field--instructions">{textInstructions}</div>}
            </div>
        </Field>
    );
}

export default memo(TextAreaField);
